/**
 * Chain configuration for wallet connections
 *
 * This file now imports from the centralized chain config.
 * All chain data is managed in src/config/chains/
 */
import type { AddEthereumChainParameter } from "@web3-react/types";
import { ChainIdEnum } from "../../enums/chain";
import {
  CHAIN_CONFIGS,
  getAddChainParameters as getCentralizedAddChainParameters,
} from "../../config/chains";

interface IBasicChainInformation {
  urls: string[];
  name: string;
}

interface IExtendedChainInformation extends IBasicChainInformation {
  nativeCurrency: AddEthereumChainParameter["nativeCurrency"];
  blockExplorerUrls: AddEthereumChainParameter["blockExplorerUrls"];
}

type ChainConfigType = {
  [chainId in ChainIdEnum]: IBasicChainInformation | IExtendedChainInformation;
};

/**
 * CHAINS object - now derived from centralized CHAIN_CONFIGS
 * Maintained for backwards compatibility
 */
export const CHAINS: ChainConfigType = Object.fromEntries(
  Array.from(CHAIN_CONFIGS.entries()).map(([chainId, config]) => [
    chainId,
    {
      urls: config.rpcUrls,
      name: config.name,
      nativeCurrency: config.nativeCurrency,
      blockExplorerUrls: [config.explorerUrl],
    },
  ])
) as ChainConfigType;

/**
 * Get AddEthereumChainParameter for wallet connection
 * Now uses centralized chain config
 */
export function getAddChainParameters(
  chainId: number
): AddEthereumChainParameter | number {
  const result = getCentralizedAddChainParameters(chainId as ChainIdEnum);

  // If result is a number, return it directly
  if (typeof result === "number") {
    return result;
  }

  // Otherwise return the full parameters
  return result as AddEthereumChainParameter;
}
